import React, { useMemo } from 'react';
import cn from 'classnames';
import Svg from '../Svg';
import useDesignStatus from './useDesignStatus';
import {
  additionalHoursBought,
  isConsultancy,
  isDesignFix,
  isRoomStyling,
} from '../ProjectStatus/utils';

// The last step of a room design or room styling: the designer's part ends with
// the shopping info form, the customer's starts with shopping. Consultancy keeps
// the old wording; Design Fix has no such step.
const shopDesignLabel = (project, forCustomer) => {
  if (isConsultancy(project.plan)) return 'Ready<br />to Shop';
  return forCustomer ? 'Shop &amp; Bring<br />to Life' : 'Shopping<br />Form Sent';
};

const STATUSES = [
  { label: 'Brief<br />Complete', value: 'complete_brief' },
  { label: 'Kickoff Call<br />Complete', value: 'video_call' },
  {
    label: project =>
      project.plan === 'mini'
        ? 'Design Direction<br />Agreed'
        : 'Layout + Design<br />Direction Agreed',
    value: 'agree_design_direction_and_room_layout',
  },
  { label: 'Design Pack<br />Sent', value: 'designer_sourcing_item' },
  // Ticked by hand after the review; approving the design does not tick it.
  { label: 'Design Review<br />Complete', value: 'final_design' },
  // Ticked by hand once the design review is done. For the designer, the label
  // itself links to the HubSpot shopping info form (see `shoppingFormUrl`);
  // opening the form does not tick the step.
  { label: shopDesignLabel, value: 'shop_design', shoppingFormLink: true },
];

const STATUS_ORDER = STATUSES.map(s => s.value);

// Same steps as a full design, relabelled. Label functions get
// (project, forCustomer): the last step reads differently for each side.
const relabel = labels => STATUSES.map(s => ({ ...s, label: labels[s.value] || s.label }));

const ROOM_STYLING_STATUSES = relabel({
  agree_design_direction_and_room_layout: 'Design Direction<br />Agreed',
  final_design: 'Design Review<br />Call Complete',
});

// Design Fix is advice only: once the brief and kickoff call are done, the
// designer sends recommendations, which is released as final_design - so,
// unlike the other two, that step is set by the release, not by a click.
const DESIGN_FIX_STATUSES = relabel({
  final_design: 'Design Recommendations<br />Sent',
})
  .filter(s => ['complete_brief', 'video_call', 'final_design'].includes(s.value))
  .map(s => (s.value === 'final_design' ? { ...s, disabled: true } : s));

const statusesFor = project => {
  const roomType = project.questionnaire && project.questionnaire.room_type;
  if (isDesignFix(roomType)) return DESIGN_FIX_STATUSES;
  if (isRoomStyling(roomType)) return ROOM_STYLING_STATUSES;
  return STATUSES;
};

const DesignStatus = ({
  project,
  editable,
  info = true,
  statusHeader = true,
  className,
  children,
  customer = false,
  customerView,
  fetchStatus = true,
  shoppingFormUrl,
}) => {
  const { status: designStatus, update } = useDesignStatus(project.id, { enabled: fetchStatus });
  const status = designStatus || project.design_process_status;
  const consultancy = isConsultancy(project.plan);
  const additionalHours = additionalHoursBought(project.additional_hours_status);
  // Designer screens pass neither prop; the customer brief screens pass both.
  const forCustomer = Boolean(customer || customerView);
  const items = useMemo(
    () => {
      const steps = statusesFor(project);
      // Rank against the full order, so a status the shorter Design Fix
      // timeline skips still ticks off every step before it.
      const statusRank = STATUS_ORDER.indexOf(status);
      const activeIndex = steps.reduce(
        (acc, s, idx) => (STATUS_ORDER.indexOf(s.value) <= statusRank ? idx : acc),
        -1,
      );
      return steps.map((s, idx) => {
        const active = idx === activeIndex;
        const disabled = s.disabled ? s.disabled : idx === 0 ? false : idx > activeIndex + 1;
        const icon = active || idx < activeIndex ? 'check' : '';
        return {
          ...s,
          label: typeof s.label === 'function' ? s.label(project, forCustomer) : s.label,
          formHref: s.shoppingFormLink && !forCustomer ? shoppingFormUrl : null,
          active,
          icon,
          selected: active || idx < activeIndex,
          disabled: editable ? disabled : true,
        };
      });
    },
    [status, editable, forCustomer, shoppingFormUrl],
  );

  return (
    <div
      className={cn('design-status', className, { '-design-status_customer-view': customerView })}
    >
      <div className="design-status_wrapper">
        {info && (
          <div
            className={cn('design-status_info', {
              '-pro': project.is_pro_platform,
            })}
          >
            <h2>{project.name}</h2>
            {project.plan_name && (
              <div className="design-status_text">
                {/*Package: {project.plan_name}*/}
                {project.questionnaire.term === 'express' ? ', Express' : ''}
              </div>
            )}

            {project.designer_full_name && (
              <div className="design-status_text">Designer: {project.designer_full_name}</div>
            )}
            {!customer && (
              <div className="design-status_text">
                Customer:
                {project.customer_full_name}
              </div>
            )}
            {additionalHours && (
              <div className="design-status_text">
                Hours bought: {project.hours_purchased} <br />
                Hours complete: {project.hours_used}
              </div>
            )}
          </div>
        )}
        {!project.is_pro_platform &&
          !consultancy && (
            <div className="design-status_pipeline">
              {statusHeader && <h2 className="design-status_heading">Project Process</h2>}
              <div className="design-status_items">
                {items.map((status, index) => (
                  <div
                    key={status.value}
                    className={cn('design-status_item', {
                      '-active': status.selected,
                      '-disabled': status.disabled,
                    })}
                    onClick={() => !status.disabled && update(status.value)}
                  >
                    <div
                      className={cn('design-status_title', {
                        '-under': true,
                        // Too many steps for side-by-side labels on a phone:
                        // every second one goes above the line (mobile only, see sass).
                        '-above': items.length > 4 && index % 2 === 1,
                        '-active': status.selected,
                      })}
                    >
                      {status.formHref ? (
                        <a
                          href={status.formHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="design-status_form-link"
                          onClick={event => event.stopPropagation()}
                          dangerouslySetInnerHTML={{ __html: status.label }}
                        />
                      ) : (
                        <div dangerouslySetInnerHTML={{ __html: status.label }} />
                      )}
                    </div>
                    <div className="design-status_line">
                      <div className="design-status_point">
                        {status.icon && (
                          <Svg
                            icon={`icon-${status.icon}`}
                            className={cn('design-status_icon', `-${status.icon}`)}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
      </div>
      {children}
    </div>
  );
};

export default DesignStatus;



// WEBPACK FOOTER //
// ./app/javascript/common/components/DesignStatus/DesignStatus.js